<script lang="ts" generics="T extends string">
  /**
   * A compact pill track of two or three short words, at the right end of a
   * row: a preference (Dark | Light), a goal (Lines | Timed), a filter (Solo |
   * Together). The checked segment is filled with the accent. TwinTen's
   * segmented control.
   *
   * It hugs its options. It is never a picker's main control and never
   * stretched to the full width: a choice with a sentence to it is a
   * `RadioCards`, a choice of two or three short names with a note each is
   * `ChoiceTiles`.
   *
   * **Its limit (1.8.0): about three short options**, a word each (some
   * eight characters at most, in the longest language the game ships). The
   * track never wraps, by design: what does not fit runs past the row's edge.
   * On a 390px phone a row has about 20rem for it on a line of its own, and
   * two short options sit beside a row's label. Four options, or longer words, are a `ChoiceTiles`
   * (tiles two or three to a row, which wrap) under the row instead.
   *
   * A real radio group for the keyboard as well as a screen reader
   * (`radioKeys.ts`): one Tab stop, and the arrow keys move and select.
   *
   * The parent owns `value`. Generic over the option values, so a
   * string-backed union comes back from `onchange` as itself.
   *
   * Test ids: `testid` on the group, each option's own `testid` on its
   * segment. No defaults.
   */
  import { handleRadioKeydown, rovingTabIndex } from '../radioKeys.js';
  import type { SegmentOption } from '../types.js';

  interface Props {
    /** The group's accessible name: what is being chosen. */
    readonly label: string;
    readonly value: T;
    readonly options: readonly SegmentOption<T>[];
    readonly onchange: (value: T) => void;
    readonly disabled?: boolean;
    readonly testid?: string | undefined;
  }
  const { label, value, options, onchange, disabled = false, testid }: Props = $props();

  let group = $state<HTMLDivElement | undefined>();
  const checkedIndex = $derived(options.findIndex((option) => option.value === value));

  function onKeydown(event: KeyboardEvent): void {
    if (disabled) return;
    handleRadioKeydown(event, group, checkedIndex, options.length, (index) => {
      const next = options[index];
      if (next !== undefined && next.value !== value) onchange(next.value);
    });
  }
</script>

<div
  class="ui-segmented"
  role="radiogroup"
  aria-label={label}
  aria-disabled={disabled ? 'true' : undefined}
  data-testid={testid}
  bind:this={group}
>
  {#each options as option, index (option.value)}
    <button
      type="button"
      role="radio"
      aria-checked={value === option.value}
      tabindex={rovingTabIndex(index, checkedIndex)}
      class="ui-segment"
      class:ui-segment--active={value === option.value}
      {disabled}
      data-testid={option.testid}
      onclick={() => {
        if (option.value !== value) onchange(option.value);
      }}
      onkeydown={onKeydown}
    >
      {option.label}
    </button>
  {/each}
</div>

<style>
  .ui-segmented {
    box-sizing: border-box;
    display: inline-flex;
    max-width: 100%;
    gap: 0.2rem;
    padding: 0.2rem;
    border-radius: var(--ui-radius-pill);
    background: var(--ui-sunken);
  }

  .ui-segment {
    position: relative;
    border: 0;
    background: transparent;
    color: var(--ui-ink-2);
    font: var(--ui-text-label);
    padding: 0.4rem 0.85rem;
    border-radius: var(--ui-radius-pill);
    white-space: nowrap;
  }

  /* A segment is short inside its track; the target reaches 2.75rem. */
  .ui-segment::before {
    content: '';
    position: absolute;
    inset: -0.4rem 0;
  }

  .ui-segment:disabled {
    opacity: 0.5;
  }

  .ui-segment--active {
    background: var(--ui-accent);
    color: var(--ui-on-accent);
  }

  .ui-segment:focus-visible {
    outline: 3px solid var(--ui-accent);
    outline-offset: 2px;
  }
</style>
